import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiCheckCircle, FiXCircle, FiEdit2 } from "react-icons/fi";
import { AirQualityCard } from "../../components/Cards";
import { IotmateButton, DialogBox } from "../../components/Shared";
import { DataHistoryTable } from "../../components/Table";
import fetchDataFromApi from "../../api/IotmateRestApi";
import { airQualityParameters, generateDummyAirQualityDetails, generateDummyHistoricalData } from "../../Data/Data";
import "./AirQualityDetails.css";

const AirQualityDetails = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [deviceData, setDeviceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [historicalData, setHistoricalData] = useState([]);

  useEffect(() => {
    let isInitialLoad = true;

    const fetchData = async () => {
      try {
        // Only show loading overlay on initial load
        if (isInitialLoad) {
          setLoading(true);
        }
        const data = await fetchDataFromApi(`air_quality_details/${deviceId}`);

        setDeviceData(data || generateDummyAirQualityDetails(deviceId));
      } catch (error) {
        console.error("Error fetching air quality details:", error);

        // Set dummy data on error
        setDeviceData(generateDummyAirQualityDetails(deviceId));
      } finally {
        if (isInitialLoad) {
          setLoading(false);
          isInitialLoad = false;
        }
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [deviceId]);

  // Fetch historical data on component mount
  useEffect(() => {
    const fetchHistoricalData = async () => {
      try {
        const data = await fetchDataFromApi(`air_quality_history/${deviceId}`);
        setHistoricalData(data || generateDummyHistoricalData(deviceId));
      } catch (error) {
        console.error("Error fetching historical data:", error);
        setHistoricalData(generateDummyHistoricalData(deviceId));
      }
    };

    fetchHistoricalData();
  }, [deviceId]);

  // Define columns for the historical data table
  const historyColumns = useMemo(
    () => [
      {
        Header: "Timestamp",
        accessor: "timestamp",
        Cell: ({ value }) => new Date(value).toLocaleString(),
      },
      {
        Header: "AQI",
        accessor: "aqi",
      },
      {
        Header: "Temperature (°C)",
        accessor: "temperature",
      },
      {
        Header: "Humidity (%)",
        accessor: "humidity",
      },
      {
        Header: "Noise (dB)",
        accessor: "noise",
      },
      {
        Header: "PM1 (µg/m³)",
        accessor: "pm1",
      },
      {
        Header: "PM2.5 (µg/m³)",
        accessor: "pm25",
      },
      {
        Header: "PM10 (µg/m³)",
        accessor: "pm10",
      },
      {
        Header: "CO₂ (ppm)",
        accessor: "co2",
      },
      {
        Header: "SO₂ (µg/m³)",
        accessor: "so2",
      },
      {
        Header: "NO₂ (µg/m³)",
        accessor: "no2",
      },
      {
        Header: "O₃ (µg/m³)",
        accessor: "o3",
      },
      {
        Header: "CO (mg/m³)",
        accessor: "co",
      },
      {
        Header: "H₂S (µg/m³)",
        accessor: "h2s",
      },
      {
        Header: "NH₃ (µg/m³)",
        accessor: "nh3",
      },
    ],
    []
  );

  const handleEditClick = () => {
    setNewName(deviceData?.name || "");
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setNewName("");
  };

  const handleSaveName = () => {
    if (newName.trim()) {
      setDeviceData({ ...deviceData, name: newName.trim() });
      setIsDialogOpen(false);
      setNewName("");
    }
  };

  if (loading) {
    return (
      <div className="aq-details-loading">
        <div className="spinner"></div>
        <p>Loading device details...</p>
      </div>
    );
  }

  if (!deviceData) {
    return (
      <div className="aq-details-error">
        <p>Failed to load device data</p>
        <button onClick={() => navigate(-1)} className="aq-details-back-btn">
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="aq-details-container">
      {/* Header */}
      <div className="aq-details-header">
        <button onClick={() => navigate(-1)} className="aq-details-back-btn">
          <FiArrowLeft size={20} />
          Back
        </button>
        <div className="aq-details-title-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h1 className="aq-details-title">
              {deviceData.name || `Air Quality Device ${deviceId}`}
            </h1>
            <IotmateButton onClick={handleEditClick} variant="default" className="iotmate-btn-icon">
              <FiEdit2 size={18} />
            </IotmateButton>
          </div>
          <div className="aq-details-status">
            {deviceData.status === "Online" ? (
              <FiCheckCircle size={24} color="#8fce00" />
            ) : (
              <FiXCircle size={24} color="#ff6b6b" />
            )}
            <span
              style={{
                color: deviceData.status === "Online" ? "#8fce00" : "#ff6b6b",
              }}
            >
              {deviceData.status || "Offline"}
            </span>
          </div>
        </div>
        <p className="aq-details-last-update">
          Last updated: {deviceData.lastUpdate || "N/A"}
        </p>
      </div>

      {/* Parameters Grid */}
      <div className="aq-details-grid">
        {airQualityParameters.map((param, index) => (
          <AirQualityCard
            key={index}
            param={{ name: param.name, icon: param.icon }}
            value={deviceData[param.key] || 0}
            unit={param.unit}
            threshold={param.threshold}
          />
        ))}
      </div>

      {/* Historical Data Table */}
      <div className="aq-details-history">
        <DataHistoryTable
          data={historicalData}
          columns={historyColumns}
          title={`${deviceData.name || 'Device'} - Historical Data`}
          dateField="timestamp"
          enableDateFilter={true}
          enableDownload={true}
        />
      </div>

      {/* Edit Name Dialog */}
      <DialogBox
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        title="Edit Device Name"
        onSave={handleSaveName}
        saveLabel="Save"
        cancelLabel="Cancel"
      >
        <input
          type="text"
          className="dialog-input"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Enter device name"
          autoFocus
        />
      </DialogBox>
    </div>
  );
};

export default AirQualityDetails;
